import React, { useRef } from 'react';
import { ChevronRightIcon } from '@fronton/icons-react';
import { Button, Link, SectionSubTitle, SwiperSlide, Swiper, Typography, Section } from 'common/components';
import { useIntersection } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  NEW_BATHROOM_COLLECTION_LIST,
  NewBathroomCollection,
  SHOW_ALL_LINK,
} from './new-bathroom-collections-constants';
import {
  newBathroomCollections,
  newBathroomCollectionsHeaderWrapper,
  newBathroomCollectionsMoreLink,
  newBathroomCollectionsTitle,
  newBathroomCollectionImage,
  newBathroomCollectionSlide,
  newBathroomCollectionSlideInfo,
  newBathroomCollectionSwiper,
  newBathroomCollectionsMoreText,
  newBathroomCollectionsMoreTextMobile,
} from './new-bathroom-collections-styles';

type NewBathroomCollectionsProps = {
  elementBlock: string;
}

export const NewBathroomCollections: React.FC<NewBathroomCollectionsProps> = ({ elementBlock }) => {
  const productRef = useRef<HTMLAnchorElement[]>([]);

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const numberPosition = Number(position);
    const collection = NEW_BATHROOM_COLLECTION_LIST[numberPosition];

    analyticsUtils.sendEvent('GA:impressions', {
      elementType: 'product',
      elementBlock,
      division: 'Ванной комнаты',
      subdivision: 'Готовые комплекты',
      komplektyId: collection.productId,
      productName: collection.title,
      productId: collection.productId,
      productPrice: null,
      productBelong: 'LeroyMerlin',
      productTag: undefined,
      productDiscount: collection.discount,
      productPrevPrice: null,
      productPosition: numberPosition + 1,
    });
  });

  const handleCardClick = (collection: NewBathroomCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock,
      division: 'Ванной комнаты',
      subdivision: 'Готовые комплекты',
      komplektyId: collection.productId,
      productName: collection.title,
      productId: collection.productId,
      productPrice: null,
      productBelong: 'LeroyMerlin',
      productTag: undefined,
      productDiscount: collection.discount,
      productPrevPrice: null,
      productPosition: position,
    });
  };

  const handleCardnWheelClick = (e: MouseEvent, collection: NewBathroomCollection, position: number) => {
    if (e.button === 1) {
      handleCardClick(collection, position);
    }
  };

  const handleSeeMoreLinkClick = () => {
    analyticsUtils.linkClickButtonEvent(elementBlock, 'Готовые комплекты', 'Смотреть все', SHOW_ALL_LINK);
  }

  const handleSeeMoreLinkWheelClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Проверка, что кликнута именно средняя кнопка/колёсико
    if (e.button === 1) {
      analyticsUtils.linkClickButtonEvent(elementBlock, 'Готовые комплекты', 'Смотреть все', SHOW_ALL_LINK);
    }
  };

  return (
    <Section>
      <div className={newBathroomCollections}>
        <div className={newBathroomCollectionsHeaderWrapper}>
          <SectionSubTitle className={newBathroomCollectionsTitle} margin="none" color="text-invert">
            Готовые комплекты
          </SectionSubTitle>
          <Link
            className={newBathroomCollectionsMoreLink}
            onClick={handleSeeMoreLinkClick}
            onAuxClick={handleSeeMoreLinkWheelClick}
            href={SHOW_ALL_LINK}
            title="Наборы мебели для ванной комнаты"
          >
            <Typography
              className={newBathroomCollectionsMoreText}
              variant="s"
              size="body_accent"
              color='text-invert'
            >
              Смотреть все
            </Typography>
            <Typography
              className={newBathroomCollectionsMoreTextMobile}
              variant="s"
              size="body_accent"
              color='text-invert'
            >
              Все
            </Typography>
            <ChevronRightIcon type="outline" size="m" color="text-invert" />
          </Link>
        </div>

        <Swiper
          breakpoints={{
            320: { enabled: true },
            728: { enabled: false },
          }}
          className={newBathroomCollectionSwiper}
          slidesPerView="auto"
          watchOverflow
        >
          {NEW_BATHROOM_COLLECTION_LIST.map((collection, index) => (
            <SwiperSlide className={newBathroomCollectionSlide} key={createElementKey('bathroom-collection', index)}>
              <Link
                refCb={(el: HTMLAnchorElement | null) => {
                  const position = el?.getAttribute('data-position');
                  if (position && el) {
                    productRef.current[+position] = el;
                  }
                }}
                data-position={index}
                href={collection.href}
                /* @ts-expect-error: param typecheck error */
                onAuxClick={e => handleCardnWheelClick(e, collection, index + 1)}
                onClick={() => handleCardClick(collection, index + 1)}
                key={createElementKey('bathroom-collection', index)}
                title={collection.title}
              >
                <img alt={collection.img.alt} className={newBathroomCollectionImage} src={collection.img.src} loading="lazy" />
                <div className={newBathroomCollectionSlideInfo}>
                  <Typography variant="s" size="subtitle" color="text-invert">
                    {collection.title}
                  </Typography>
                  <Typography variant="minor_caption" color="text-secondary-invert">
                    {collection.description}
                  </Typography>
                </div>
                <Button invert variant="secondary" size="m" wide>
                  Подробнее
                </Button>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </Section>
  );
}
